/**
 * Writes `tEXt` metadata into a PNG the app has just encoded — no dependency, the
 * same hand-rolled posture as the chart rasterizer and the table's CSV export.
 *
 * Why this exists: a chart Alpinist produced is artificially generated content, and
 * Art. 50(2) of the EU AI Act asks providers of a system that generates such content
 * to mark the output "in a machine-readable format and detectable as artificially
 * generated". A file name is not a marking and a caption in the page is not either —
 * the marking has to travel with the bytes, because the whole point is the file that
 * leaves the chat and lands in an email or a shift report.
 *
 * `tEXt` is the interoperable choice: exiftool, ImageMagick, Pillow and every PNG
 * chunk reader surface these keys today, whereas C2PA content credentials need a
 * signing identity we do not have. The values are deliberately ASCII and NOT
 * localized — a machine reader wants one stable string, not four.
 *
 * PNG layout, for the splice below: an 8-byte signature, then chunks of
 * `[length uint32][type 4 bytes][data][crc uint32]`, where the CRC covers type+data.
 * `IHDR` is always first; ancillary chunks like `tEXt` are legal anywhere after it,
 * so the new chunks go directly behind it.
 */

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]

/** What we stamp on every chart PNG the chat exports. */
export const AI_PROVENANCE_TEXT: PngTextEntry[] = [
  {keyword: 'Software', text: 'Alpinist - MontBlancAI'},
  // A single unambiguous key/value, so a checker can answer "was this AI-generated?"
  // without parsing prose.
  {keyword: 'AI-Generated', text: 'true'},
  {
    keyword: 'Comment',
    text:
      'AI-generated chart, created by the Alpinist AI assistant (MontBlancAI). ' +
      'Artificially generated content - verify against the source data before acting on it.',
  },
]

export interface PngTextEntry {
  /** Latin-1, 1-79 bytes, no leading/trailing or repeated spaces (PNG spec). */
  keyword: string
  /** Latin-1, no NUL. */
  text: string
}

let crcTable: Uint32Array | null = null

/** The PNG/zlib CRC-32 (reflected, polynomial 0xEDB88320). */
function crc32(bytes: Uint8Array): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256)
    for (let n = 0; n < 256; n++) {
      let c = n
      for (let k = 0; k < 8; k++) {
        c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
      }
      crcTable[n] = c >>> 0
    }
  }
  let crc = 0xffffffff
  for (let i = 0; i < bytes.length; i++) {
    crc = crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8)
  }
  return (crc ^ 0xffffffff) >>> 0
}

/** Latin-1 bytes. Anything outside the range would corrupt a `tEXt` field, so it throws. */
function latin1(value: string, field: string): Uint8Array {
  const out = new Uint8Array(value.length)
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i)
    if (code === 0 || code > 0xff) {
      throw new Error(`PNG tEXt ${field} must be Latin-1 without NUL: ${JSON.stringify(value)}`)
    }
    out[i] = code
  }
  return out
}

/** One complete `tEXt` chunk: length + type + (keyword NUL text) + crc. */
export function buildTextChunk(entry: PngTextEntry): Uint8Array {
  const keyword = latin1(entry.keyword, 'keyword')
  if (keyword.length < 1 || keyword.length > 79) {
    throw new Error(`PNG tEXt keyword must be 1-79 bytes: ${entry.keyword}`)
  }
  const text = latin1(entry.text, 'text')

  const type = latin1('tEXt', 'type')
  const data = new Uint8Array(keyword.length + 1 + text.length)
  data.set(keyword, 0)
  data[keyword.length] = 0
  data.set(text, keyword.length + 1)

  const chunk = new Uint8Array(4 + type.length + data.length + 4)
  const view = new DataView(chunk.buffer)
  view.setUint32(0, data.length)
  chunk.set(type, 4)
  chunk.set(data, 8)

  const forCrc = new Uint8Array(type.length + data.length)
  forCrc.set(type, 0)
  forCrc.set(data, type.length)
  view.setUint32(8 + data.length, crc32(forCrc))

  return chunk
}

function isPng(bytes: Uint8Array): boolean {
  return PNG_SIGNATURE.every((b, i) => bytes[i] === b)
}

/** Byte offset just past the IHDR chunk, where ancillary chunks may be inserted. */
function offsetAfterIhdr(bytes: Uint8Array): number {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const ihdrLength = view.getUint32(8)
  // signature + length + type + data + crc
  return 8 + 4 + 4 + ihdrLength + 4
}

/** Insert `tEXt` chunks into PNG bytes. Returns the original bytes if it is not a PNG. */
export function withPngTextChunksBytes(bytes: Uint8Array, entries: PngTextEntry[]): Uint8Array {
  if (!isPng(bytes) || entries.length === 0) return bytes

  const insertAt = offsetAfterIhdr(bytes)
  if (insertAt >= bytes.length) return bytes

  const chunks = entries.map(buildTextChunk)
  const added = chunks.reduce((sum, c) => sum + c.length, 0)

  const out = new Uint8Array(bytes.length + added)
  out.set(bytes.subarray(0, insertAt), 0)
  let cursor = insertAt
  for (const chunk of chunks) {
    out.set(chunk, cursor)
    cursor += chunk.length
  }
  out.set(bytes.subarray(insertAt), cursor)
  return out
}

/**
 * Blob wrapper around {@link withPngTextChunksBytes}.
 *
 * Note for the clipboard path: we mark the blob we hand to `ClipboardItem`, but the OS
 * clipboard may re-encode the image and drop ancillary chunks. That is outside our
 * reach — the file that gets downloaded and forwarded is the one that matters, and it
 * keeps the marking.
 */
export async function withPngTextChunks(blob: Blob, entries: PngTextEntry[]): Promise<Blob> {
  const bytes = new Uint8Array(await blob.arrayBuffer())
  const marked = withPngTextChunksBytes(bytes, entries)
  if (marked === bytes) return blob
  return new Blob([marked], {type: blob.type || 'image/png'})
}

/** Read back the `tEXt` chunks of a PNG. Used by the tests, and handy when debugging. */
export function readPngTextChunks(bytes: Uint8Array): PngTextEntry[] {
  if (!isPng(bytes)) return []
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const entries: PngTextEntry[] = []

  let offset = 8
  while (offset + 8 <= bytes.length) {
    const length = view.getUint32(offset)
    const type = String.fromCharCode(...bytes.subarray(offset + 4, offset + 8))
    if (type === 'tEXt') {
      const data = bytes.subarray(offset + 8, offset + 8 + length)
      const split = data.indexOf(0)
      if (split > 0) {
        entries.push({
          keyword: String.fromCharCode(...data.subarray(0, split)),
          text: String.fromCharCode(...data.subarray(split + 1)),
        })
      }
    }
    if (type === 'IEND') break
    offset += 12 + length
  }
  return entries
}
